// Panel de créditos de una empresa (admin > Configurar empresa): saldo,
// paquetes asignados, formulario para agregar créditos y consumos recientes.
// Las reglas de cobro viven en backend/src/services/creditosService.js.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Coins, Plus, Ban, RefreshCw } from 'lucide-react';

const ESTADOS = {
  vigente: 'Vigente',
  vencido: 'Vencido',
  agotado: 'Agotado',
  anulado: 'Retirado'
};

const fechaCorta = (v) => (v ? new Date(v).toLocaleDateString('es-MX') : '—');
const textoCreditos = (n) => `${n} crédito${n === 1 ? '' : 's'}`;

const FORM_INICIAL = { cantidad: '', vigencia: '12', fecha: '', nota: '' };

const CreditosEmpresa = ({ empresaId, ilimitados, showModal, onCambio }) => {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [form, setForm] = useState(FORM_INICIAL);
  const [guardando, setGuardando] = useState(false);
  // showModal se recrea en cada render del Dashboard; con un ref no reinicia la carga
  const showModalRef = useRef(showModal);
  showModalRef.current = showModal;

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const response = await fetch(`/api/empresas/${empresaId}/creditos`, { credentials: 'include' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'No se pudieron cargar los créditos');
      setDatos(data);
    } catch (error) {
      showModalRef.current({ type: 'error', title: 'Error al cargar créditos', message: error.message });
    } finally {
      setCargando(false);
    }
  }, [empresaId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const handleAgregar = async (e) => {
    e.preventDefault();
    const cantidad = parseInt(form.cantidad, 10);
    if (!Number.isInteger(cantidad) || cantidad < 1) {
      showModal({ type: 'warning', title: 'Cantidad inválida', message: 'Indica cuántos créditos quieres asignar (mínimo 1).' });
      return;
    }
    if (form.vigencia === 'fecha' && !form.fecha) {
      showModal({ type: 'warning', title: 'Fecha requerida', message: 'Indica la fecha de vencimiento de los créditos.' });
      return;
    }

    const body = { cantidad, nota: form.nota };
    if (form.vigencia === 'fecha') body.vence_at = form.fecha;
    else if (form.vigencia !== 'sin') body.vigencia_meses = parseInt(form.vigencia, 10);

    setGuardando(true);
    try {
      const response = await fetch(`/api/empresas/${empresaId}/creditos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'No se pudieron asignar los créditos');
      setForm(FORM_INICIAL);
      await cargar();
      if (onCambio) onCambio();
    } catch (error) {
      showModal({ type: 'error', title: 'Error al asignar créditos', message: error.message });
    } finally {
      setGuardando(false);
    }
  };

  const handleAnular = (paquete) => {
    showModal({
      type: 'warning',
      title: 'Retirar créditos',
      showConfirmButton: true,
      showCancelButton: true,
      confirmText: 'Retirar',
      message: `Se retirarán los ${textoCreditos(paquete.restantes)} que quedan de este paquete. Lo ya consumido no cambia. ¿Continuar?`,
      onConfirm: async () => {
        try {
          const response = await fetch(`/api/empresas/${empresaId}/creditos/${paquete.id}/anular`, {
            method: 'POST',
            credentials: 'include'
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.error || 'No se pudo retirar el paquete');
          await cargar();
          if (onCambio) onCambio();
        } catch (error) {
          showModal({ type: 'error', title: 'Error al retirar créditos', message: error.message });
        }
      }
    });
  };

  const saldo = datos?.saldo;

  return (
    <div className="empresa-acceso-box creditos-empresa">
      <div className="creditos-empresa-header">
        <h3><Coins size={15} /> Créditos</h3>
        <button type="button" className="btn-icon" onClick={cargar} disabled={cargando} title="Actualizar">
          <RefreshCw size={13} />
        </button>
      </div>
      <p className="form-hint">
        Una prueba individual cuesta 1 crédito y un stack de hasta 3 pruebas cuesta 2. El costo se reserva al
        invitar y se cobra cuando el candidato inicia la evaluación; si la invitación vence sin iniciarse, se libera.
      </p>

      {ilimitados ? (
        <p className="creditos-resumen"><strong>Créditos ilimitados</strong> — esta empresa no consume saldo.</p>
      ) : saldo && (
        <div className="creditos-resumen">
          <span><strong>{saldo.libres}</strong> libres</span>
          <span><strong>{saldo.reservados}</strong> reservados ({saldo.invitaciones_pendientes} invitaci{saldo.invitaciones_pendientes === 1 ? 'ón' : 'ones'} sin iniciar)</span>
          <span><strong>{saldo.disponibles}</strong> en saldo</span>
          {saldo.proximo_vencimiento && (
            <span>{textoCreditos(saldo.proximo_vencimiento.cantidad)} vence{saldo.proximo_vencimiento.cantidad === 1 ? '' : 'n'} el {fechaCorta(saldo.proximo_vencimiento.fecha)}</span>
          )}
        </div>
      )}

      <form className="creditos-form" onSubmit={handleAgregar}>
        <div className="form-group">
          <label>Créditos a asignar</label>
          <input
            type="number"
            min="1"
            max="100000"
            value={form.cantidad}
            onChange={(e) => setForm({ ...form, cantidad: e.target.value })}
            placeholder="Ej: 40"
          />
        </div>
        <div className="form-group">
          <label>Vigencia</label>
          <select value={form.vigencia} onChange={(e) => setForm({ ...form, vigencia: e.target.value })}>
            <option value="6">6 meses</option>
            <option value="12">12 meses</option>
            <option value="1">1 mes</option>
            <option value="sin">Sin vencimiento</option>
            <option value="fecha">Hasta una fecha…</option>
          </select>
        </div>
        {form.vigencia === 'fecha' && (
          <div className="form-group">
            <label>Vencen el</label>
            <input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} />
          </div>
        )}
        <div className="form-group creditos-form-nota">
          <label>Nota (opcional)</label>
          <input
            type="text"
            maxLength="300"
            value={form.nota}
            onChange={(e) => setForm({ ...form, nota: e.target.value })}
            placeholder="Ej: Plan Profesional, pago #1234"
          />
        </div>
        <button type="submit" className="btn-success" disabled={guardando}>
          <Plus size={14} /> {guardando ? 'Asignando…' : 'Asignar créditos'}
        </button>
      </form>

      <h4 className="empresa-acceso-subtitle">Paquetes asignados</h4>
      {!datos || datos.paquetes.length === 0 ? (
        <p className="text-muted">{cargando ? 'Cargando…' : 'Aún no se le han asignado créditos.'}</p>
      ) : (
        <div className="stack-list">
          {datos.paquetes.map(p => (
            <div key={p.id} className="stack-row">
              <div className="stack-row-info">
                <strong>{p.restantes} de {textoCreditos(p.cantidad)}</strong>
                <small>
                  Asignado el {fechaCorta(p.created_at)}
                  {p.creado_por ? ` por ${p.creado_por}` : p.origen === 'registro' ? ' (registro)' : p.origen === 'migracion' ? ' (conversión de cupo)' : ''}
                  {' · '}{p.vence_at ? `vence el ${fechaCorta(p.vence_at)}` : 'sin vencimiento'}
                  {p.nota ? ` · ${p.nota}` : ''}
                </small>
                <span className={`badge-count ${p.estado === 'vigente' ? '' : 'badge-count-warn'}`}>{ESTADOS[p.estado] || p.estado}</span>
              </div>
              {p.estado === 'vigente' && (
                <div className="stack-row-actions">
                  <button type="button" className="btn-icon btn-icon-danger" onClick={() => handleAnular(p)} title="Retirar lo que queda">
                    <Ban size={13} /> Retirar
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <h4 className="empresa-acceso-subtitle">Consumos recientes</h4>
      {!datos || datos.consumos.length === 0 ? (
        <p className="text-muted">Sin consumos todavía.</p>
      ) : (
        <ul className="creditos-consumos">
          {datos.consumos.map(c => (
            <li key={c.asignacion_id ?? c.fecha}>
              <span>{fechaCorta(c.fecha)}</span>
              <span>{c.candidato || 'Candidato eliminado'} · {c.stack || 'Stack eliminado'}</span>
              <strong>−{c.cantidad}</strong>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CreditosEmpresa;
